import { useMemo, useRef } from 'react';
import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { SwitchInputGroup } from '@interxfi/design-system/components/input/switch-input/group/switch-input-group';
import { useIsElementVisible } from '@interxfi/design-system/util/visibility';
import { type IxcRegistrationV1ControlPersonOfficer, IxcRegistrationV1ProfileStatus } from '@interxfi/types/generated/api';
import {
  buildProfileListQuery,
  RegistrationProfileComboboxWithLens,
} from '../../../../../../components/data-combobox-input/entity-config/registration-profile';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../../util/has-value';

export const officerControlPersonFormSchema = z.object({
  kind: z.literal('officer'),
  value: z.object({
    profileId: z.string().nonempty('Profile is required'),
    title: z.string(),
    signer: z.boolean(),
  }),
  primaryContact: z.boolean(),
});

export type OfficerControlPersonFormValues = z.infer<typeof officerControlPersonFormSchema>;

export const officerControlPersonFormDefaultValues = {
  kind: 'officer',
  value: {
    profileId: '',
    title: '',
    signer: false,
  },
  primaryContact: false,
} as const satisfies OfficerControlPersonFormValues;

export function formValuesToOfficerControlPerson(values: OfficerControlPersonFormValues): IxcRegistrationV1ControlPersonOfficer {
  return addDefinedValues<IxcRegistrationV1ControlPersonOfficer>(
    { profileId: values.value.profileId, signer: values.value.signer },
    { title: values.value.title },
  );
}

export function getInitialFormValuesForOfficerControlPerson(
  data: IxcRegistrationV1ControlPersonOfficer | undefined,
  primaryContactId: string | undefined,
): OfficerControlPersonFormValues {
  return {
    ...officerControlPersonFormDefaultValues,
    value: {
      ...officerControlPersonFormDefaultValues.value,
      profileId: data?.profileId ?? '',
      title: data?.title ?? '',
      signer: Boolean(data?.signer),
    },
    primaryContact: Boolean(data?.profileId && data.profileId === primaryContactId),
  };
}

interface OfficerControlPersonFormProps {
  lens: Lens<OfficerControlPersonFormValues>;
  programId: string | undefined;
}

export function OfficerControlPersonForm({ lens, programId }: OfficerControlPersonFormProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsElementVisible(ref);
  const profileSelectRequest = useMemo(() => {
    if (!programId) {
      return null;
    }

    return buildProfileListQuery({ programId, status: IxcRegistrationV1ProfileStatus.Active });
  }, [programId]);

  return (
    <VerticalStack gap={4} ref={ref}>
      <RegistrationProfileComboboxWithLens
        label="Profile"
        lens={lens.focus('value.profileId')}
        queryOptions={profileSelectRequest}
        required={isVisible}
      />

      <SwitchInputGroup>
        <AppSwitchInputWithLens label="Signer" lens={lens.focus('value.signer')} />
        <AppSwitchInputWithLens label="Primary Contact" lens={lens.focus('primaryContact')} />
      </SwitchInputGroup>

      <AppTextInputWithLens label="Title" lens={lens.focus('value.title')} />
    </VerticalStack>
  );
}
